
import React from "react";
import { CalendarDays } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { advancedTierSchema } from "@/contexts/program-creation/types";

interface PeriodizationSectionProps {
  form: UseFormReturn<z.infer<typeof advancedTierSchema>>;
}

export default function PeriodizationSection({ form }: PeriodizationSectionProps) {
  return (
    <div className="space-y-5 mb-8">
      <div className="flex items-center gap-2 text-white/90 border-b border-white/20 pb-2">
        <CalendarDays size={18} />
        <h4 className="font-medium">Periodization</h4>
      </div>
      
      <FormField
        control={form.control}
        name="hasPeriodizationExperience"
        render={({ field }) => (
          <FormItem className="space-y-3">
            <FormLabel className="text-white/90 text-sm">
              Has the client used periodization in their training – structuring their training into phases (e.g., strength phase, hypertrophy phase)?
            </FormLabel>
            <FormControl>
              <RadioGroup 
                onValueChange={field.onChange} 
                defaultValue={field.value}
                className="flex flex-col space-y-1"
              >
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="yes" />
                  </FormControl>
                  <FormLabel className="font-normal text-white">
                    Yes, they have experience with periodization
                  </FormLabel>
                </FormItem>
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="no" />
                  </FormControl>
                  <FormLabel className="font-normal text-white">
                    No, they maintain a steady routine without periodization
                  </FormLabel>
                </FormItem>
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="not_sure" />
                  </FormControl>
                  <FormLabel className="font-normal text-white">
                    Not sure about their periodization experience
                  </FormLabel>
                </FormItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {form.watch("hasPeriodizationExperience") === "yes" && (
        <FormField
          control={form.control}
          name="periodizationApproach"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white/90 text-sm">
                What type of periodization have they used (linear, undulating, block, etc.) and what phase are they currently in?
              </FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="E.g., Linear periodization (currently in strength phase), Undulating periodization, Block periodization..." 
                  className="bg-white/20 border-white/30 text-white min-h-[80px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
